export const MAX_PULL_PAGES_PER_TICK = 100;

export interface PullCursorState {
  byTenant: Record<string, number>;
  legacy: number;
  sawPerTenantResponse: boolean;
}

export interface PullPageCursor {
  max_rev: number;
  max_rev_by_tenant?: Record<string, number>;
  has_more: boolean;
  stale_cursor?: boolean;
}

export interface PullDrainResult<Page extends PullPageCursor> {
  finalPage: Page;
  cursor: PullCursorState;
  pages: number;
}

function safeRevision(value: unknown): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
}

export function initialPullCursor(
  byTenantInput: Record<string, unknown>,
  legacyInput: unknown,
): PullCursorState {
  const byTenant: Record<string, number> = {};
  for (const [tenantId, value] of Object.entries(byTenantInput)) {
    if (!tenantId) continue;
    byTenant[tenantId] = safeRevision(value);
  }
  const legacy = Math.max(
    safeRevision(legacyInput),
    0,
    ...Object.values(byTenant),
  );
  return { byTenant, legacy, sawPerTenantResponse: false };
}

export function advancePullCursor(
  current: PullCursorState,
  page: PullPageCursor,
): PullCursorState {
  if (page.max_rev_by_tenant) {
    const byTenant = { ...current.byTenant };
    for (const [tenantId, rawRevision] of Object.entries(page.max_rev_by_tenant)) {
      const revision = safeRevision(rawRevision);
      byTenant[tenantId] = page.stale_cursor
        ? revision
        : Math.max(byTenant[tenantId] ?? 0, revision);
    }
    return {
      byTenant,
      legacy: Math.max(0, ...Object.values(byTenant)),
      sawPerTenantResponse: true,
    };
  }
  return {
    ...current,
    legacy: page.stale_cursor
      ? safeRevision(page.max_rev)
      : Math.max(current.legacy, safeRevision(page.max_rev)),
  };
}

function cursorProgressed(before: PullCursorState, after: PullCursorState): boolean {
  if (after.legacy !== before.legacy) return true;
  const tenants = new Set([
    ...Object.keys(before.byTenant),
    ...Object.keys(after.byTenant),
  ]);
  for (const tenantId of tenants) {
    if ((before.byTenant[tenantId] ?? 0) !== (after.byTenant[tenantId] ?? 0)) {
      return true;
    }
  }
  return false;
}

/**
 * Drain pull pages and checkpoint each cursor only after its page was applied.
 * A safety-limit error therefore resumes from the last complete page on the
 * next tick instead of replaying the same prefix forever. A crash between apply
 * and checkpoint may repeat that page, so callers must use idempotent upserts.
 */
export async function drainPullPages<Page extends PullPageCursor>(input: {
  initialCursor: PullCursorState;
  fetchPage: (cursor: PullCursorState, pageIndex: number) => Promise<Page>;
  applyPage: (page: Page, pageIndex: number) => Promise<void>;
  commitCursor: (cursor: PullCursorState, finalPage: Page) => Promise<void>;
  maxPages?: number;
}): Promise<PullDrainResult<Page>> {
  const maxPages = input.maxPages ?? MAX_PULL_PAGES_PER_TICK;
  if (!Number.isInteger(maxPages) || maxPages <= 0) {
    throw new Error('sync_pull_invalid_page_limit');
  }

  let cursor = input.initialCursor;
  for (let pageIndex = 0; pageIndex < maxPages; pageIndex += 1) {
    const page = await input.fetchPage(cursor, pageIndex);
    const nextCursor = advancePullCursor(cursor, page);
    await input.applyPage(page, pageIndex);

    if (page.has_more && !cursorProgressed(cursor, nextCursor)) {
      throw new Error('sync_pull_cursor_did_not_advance');
    }
    // Never advance the durable cursor before the corresponding page writes
    // are complete. This is an intermediate revision while has_more=true, not
    // the server head advertised by the terminal response.
    await input.commitCursor(nextCursor, page);
    if (!page.has_more) {
      return {
        finalPage: page,
        cursor: nextCursor,
        pages: pageIndex + 1,
      };
    }
    cursor = nextCursor;
  }

  throw new Error(`sync_pull_page_limit_exceeded:${maxPages}`);
}
